/*
 * <price-match> — the "Request a price match" card, modal form and submission.
 *
 * Rendered by snippets/price-match.liquid, styled by component-price-match.css.
 *
 * Why this element updates itself instead of being re-rendered:
 * <product-info> (assets/product-info.js) does an id-scoped, in-place Section
 * Rendering API update for only seven ids — price, Sku, Barcode, Inventory,
 * Badges, PricePerItem, Volume. `PriceMatch-<section>` is deliberately not one of
 * them, so this element's DOM survives a variant change untouched and has to
 * re-sync its hidden fields and re-check eligibility from the variant map that
 * Liquid emitted. (On the product-*swap* path the whole section is replaced,
 * which re-renders this element server-side with correct state, so both paths
 * are covered.)
 *
 * Two variant signals, not one. `variant:changed` is the obvious one, but
 * product-info.js returns early into setUnavailable() when no variant resolves
 * (product-info.js:256-259) and never dispatches it — so an unbuyable option
 * combination would leave a stale card on screen. updateVariantInputs()
 * (product-info.js:370-378) always fires a bubbling `change` on the form's
 * [name="id"] input with `value = variantId ?? ''`, on both paths, so that is the
 * primary signal here. Both funnel through applyVariant(), deduped on the last
 * applied id, so a double-fire is a no-op.
 *
 * The modal is the theme's <basic-modal>. Note that it re-parents itself to
 * document.body while open (ModalComponent.requiresBodyAppended), so every node
 * inside it is resolved once on connect and held — querying through `this` after
 * the first open would find nothing.
 */
if (!customElements.get('price-match')) {
  const HCAPTCHA_CALLBACK = 'priceMatchHcaptchaOnload';
  const HCAPTCHA_SRC = `https://js.hcaptcha.com/1/api.js?render=explicit&onload=${HCAPTCHA_CALLBACK}`;

  // Shared across every instance on the page (main product + quick view), so the
  // third-party script is fetched at most once and only after someone actually
  // opens a form.
  let hcaptchaPromise = null;

  const loadHcaptcha = () => {
    if (window.hcaptcha) return Promise.resolve(window.hcaptcha);
    if (hcaptchaPromise) return hcaptchaPromise;

    hcaptchaPromise = new Promise((resolve, reject) => {
      window[HCAPTCHA_CALLBACK] = () => resolve(window.hcaptcha);
      const script = document.createElement('script');
      script.src = HCAPTCHA_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('hCaptcha script failed to load'));
      document.head.appendChild(script);
    });

    return hcaptchaPromise;
  };

  customElements.define(
    'price-match',
    class PriceMatch extends HTMLElement {
      constructor() {
        super();

        this.messages = {
          required: 'This field is required.',
          email: 'Enter a valid email address.',
          url: 'Enter the full web address, starting with https://',
          price: 'Enter the competitor’s price as a number, for example 129.99.',
          consent: 'Please confirm the request meets the conditions above.',
          captcha: 'Please complete the verification before submitting.',
          failed: 'Sorry, we couldn’t send your request just now. Please try again in a moment.',
        };

        this.appliedVariantId = null;
        this.captchaWidgetId = null;

        this.onVariantChanged = this.onVariantChanged.bind(this);
        this.onFormIdInputChange = this.onFormIdInputChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onFieldInput = this.onFieldInput.bind(this);
        this.onModalShown = this.onModalShown.bind(this);
      }

      connectedCallback() {
        // Everything below the modal is resolved once, here: <basic-modal> moves
        // itself into document.body when it opens, taking the form with it.
        this.modal = this.querySelector('basic-modal');
        this.form = this.querySelector('[data-price-match-form]');
        if (!this.form) return;

        this.submitButton = this.form.querySelector('[data-price-match-submit]');
        this.errorBanner = this.form.querySelector('[data-price-match-error]');
        this.errorBannerText = this.form.querySelector('[data-price-match-error-text]');
        this.successPanel = this.querySelector('[data-price-match-success]');
        this.captchaContainer = this.form.querySelector('[data-price-match-captcha]');
        this.honeypot = this.field('pm_website');

        this.webhookUrl = this.dataset.webhookUrl || '';
        this.hcaptchaSiteKey = this.dataset.hcaptchaSiteKey || '';
        this.variants = this.parseVariants();

        this.form.addEventListener('submit', this.onSubmit);
        this.form.addEventListener('input', this.onFieldInput);
        this.form.addEventListener('change', this.onFieldInput);

        document.addEventListener('variant:changed', this.onVariantChanged);
        // Delegated rather than bound to the input directly: product-info.js
        // view-transitions the variant picker and can replace the form's markup.
        document.addEventListener('change', this.onFormIdInputChange);

        if (this.hcaptchaSiteKey && this.captchaContainer && this.modal) {
          this.modal.addEventListener('modal:handleAfterShow', this.onModalShown);
        }

        this.applyVariant(this.dataset.selectedVariant);
      }

      disconnectedCallback() {
        document.removeEventListener('variant:changed', this.onVariantChanged);
        document.removeEventListener('change', this.onFormIdInputChange);
        this.modal?.removeEventListener('modal:handleAfterShow', this.onModalShown);
      }

      /* ========================
         DATA
      ========================= */

      // namedItem() rather than form.elements.<name>: property access on a form
      // collection is shadowed by anything already on HTMLCollection.prototype,
      // and this form has fields called `name` and `length`-adjacent things are
      // one rename away.
      field(name) {
        return this.form.elements.namedItem(name);
      }

      // Malformed JSON hides the card rather than throwing — a broken variant map
      // must never take the rest of the product page down with it.
      parseVariants() {
        const script = this.querySelector('[data-price-match-variants]');
        if (!script) return {};

        try {
          return JSON.parse(script.textContent) || {};
        } catch (error) {
          console.warn('[price-match] could not parse variant map', error);
          return {};
        }
      }

      /* ========================
         VARIANT SYNC
      ========================= */

      onVariantChanged(event) {
        const variant = event.detail && event.detail.variant;
        // No id, or an id this product doesn't have, means the event came from
        // somewhere else — a quick view of another product, most likely, since
        // `variant:changed` carries no section id.
        if (!variant || !this.variants[String(variant.id)]) return;
        this.applyVariant(variant.id);
      }

      onFormIdInputChange(event) {
        const input = event.target;
        if (!input || input.name !== 'id') return;

        // input.form, not closest('form'): buy-buttons.liquid renders some controls
        // outside the form element and associates them with a form="" attribute.
        const productForm = input.form;
        if (!productForm || productForm.id !== this.dataset.productFormId) return;

        this.applyVariant(input.value);
      }

      // The single "given a variant, what does this block look like?" entry point.
      // An empty or unknown id means nothing is selectable, which is not something
      // we can price match, so the card hides.
      applyVariant(variantId) {
        const id = variantId ? String(variantId) : '';
        if (id === this.appliedVariantId) return;
        this.appliedVariantId = id;

        const variant = id ? this.variants[id] : null;

        if (variant) {
          this.setHiddenField('variant_id', id);
          this.setHiddenField('variant_title', variant.title || '');
          this.setHiddenField('sku', variant.sku || '');
          this.setHiddenField('barcode', variant.barcode || '');
          this.setHiddenField('overkill_price', variant.price == null ? '' : String(variant.price));
          this.setHiddenField('overkill_price_formatted', variant.price_formatted || '');
        }

        const eligible = Boolean(variant && variant.eligible);
        this.hidden = !eligible;

        // Can't happen through the UI — the open modal covers the variant picker —
        // but a programmatic variant change shouldn't leave a form open for a
        // variant that no longer qualifies.
        if (!eligible && this.modal?.open) this.modal.hide();
      }

      setHiddenField(name, value) {
        const field = this.field(name);
        if (field) field.value = value;
      }

      /* ========================
         VALIDATION
      ========================= */

      onFieldInput(event) {
        const field = event.target;
        if (!field || !field.name) return;
        field.removeAttribute('aria-invalid');
        this.setFieldError(field, '');
      }

      setFieldError(field, message) {
        const wrapper = field.closest('.form-field');
        const node = wrapper && wrapper.querySelector('[data-price-match-field-error]');
        if (!node) return;

        node.textContent = message;
        node.classList.toggle('hidden', !message);
      }

      clearErrors() {
        this.form.querySelectorAll('[data-price-match-field-error]').forEach((node) => {
          node.textContent = '';
          node.classList.add('hidden');
        });
        this.form.querySelectorAll('[aria-invalid]').forEach((field) => field.removeAttribute('aria-invalid'));
        this.hideBanner();
      }

      // Returns the first invalid field, or null when everything passes. Messages
      // are written as it goes so the shopper sees every problem at once.
      validate() {
        let firstInvalid = null;

        const fail = (field, message) => {
          field.setAttribute('aria-invalid', 'true');
          this.setFieldError(field, message);
          if (!firstInvalid) firstInvalid = field;
        };

        const nameField = this.field('name');
        if (!nameField.value.trim()) fail(nameField, this.messages.required);

        const emailField = this.field('email');
        if (!emailField.value.trim()) {
          fail(emailField, this.messages.required);
        } else if (!emailField.checkValidity()) {
          fail(emailField, this.messages.email);
        }

        const urlField = this.field('competitor_url');
        if (!urlField.value.trim()) {
          fail(urlField, this.messages.required);
        } else if (!this.isHttpUrl(urlField.value.trim())) {
          fail(urlField, this.messages.url);
        }

        const priceField = this.field('competitor_price');
        const rawPrice = priceField.value.trim();
        const price = parseFloat(rawPrice);
        if (!rawPrice) {
          fail(priceField, this.messages.required);
        } else if (!Number.isFinite(price) || price <= 0) {
          fail(priceField, this.messages.price);
        }

        const consentField = this.field('eligibility_confirmed');
        if (!consentField.checked) fail(consentField, this.messages.consent);

        return firstInvalid;
      }

      isHttpUrl(value) {
        try {
          const parsed = new URL(value);
          return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        } catch (error) {
          return false;
        }
      }

      /* ========================
         SUBMISSION
      ========================= */

      onSubmit(event) {
        event.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Honeypot first, and silently: a bot that filled it gets the same
        // confirmation a human would, with nothing sent and no clue why.
        if (this.honeypot && this.honeypot.value.trim()) {
          this.showSuccess();
          return;
        }

        this.clearErrors();

        const firstInvalid = this.validate();
        if (firstInvalid) {
          firstInvalid.focus();
          return;
        }

        let captchaToken = '';
        if (this.hcaptchaSiteKey) {
          captchaToken = this.getCaptchaToken();
          if (!captchaToken) {
            this.showBanner(this.messages.captcha);
            return;
          }
        }

        this.send(this.buildPayload(captchaToken));
      }

      // Flatten a string so it can be dropped verbatim into a hand-written JSON body
      // by a system that does not escape for us (Make, and anything like it).
      // Exactly three things can break a JSON string literal: a double quote, a
      // backslash, and a control character. All three are neutralised here. Missing
      // the backslash was a real bug — a shopper typing one produced
      // `a backslash \ too` and an unparseable request body.
      flattenForJson(input) {
        return String(input ?? '')
          .trim()
          .replace(/\\/g, '')
          .replace(/"/g, "'")
          .replace(/[\u0000-\u001F\u007F]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }

      buildPayload(captchaToken) {
        const value = (name) => this.field(name).value;
        const variantId = value('variant_id');
        const productUrl = this.dataset.productUrl || '';

        // JSON.stringify turns NaN into null, which would reach the automation as
        // a silently missing price. Shouldn't happen — the block only renders for
        // variants that have one — but be explicit about it rather than lucky.
        const overkillPrice = parseFloat(value('overkill_price'));

        return {
          form: 'price_match_request',
          submitted_at: new Date().toISOString(),
          name: value('name').trim(),
          email: value('email').trim(),
          competitor_url: value('competitor_url').trim(),
          competitor_price: parseFloat(value('competitor_price')),
          notes: value('notes').trim(),
          // A flattened copy of the notes for systems that interpolate values into
          // a hand-written JSON body and so cannot cope with a raw newline or
          // quote. `notes` above stays exactly as the shopper typed it — this is an
          // extra field, not a replacement, so nothing downstream loses the
          // original. Line breaks become spaces and double quotes become single
          // ones; the meaning survives, which is all the reviewer needs.
          notes_plain: this.flattenForJson(value('notes')),
          eligibility_confirmed: this.field('eligibility_confirmed').checked,
          product_title: value('product_title'),
          // Same reason as notes_plain: the intake scenario drops this straight into a
          // hand-written JSON body, and product names really do contain quotes.
          product_title_plain: this.flattenForJson(value('product_title')),
          product_handle: value('product_handle'),
          product_url: variantId ? `${productUrl}?variant=${variantId}` : productUrl,
          variant_id: variantId,
          variant_title: value('variant_title'),
          sku: value('sku'),
          barcode: value('barcode'),
          overkill_price: Number.isFinite(overkillPrice) ? overkillPrice : null,
          overkill_price_formatted: value('overkill_price_formatted'),
          currency: value('currency'),
          customer_id: value('customer_id'),
          page_url: window.location.href,
          hcaptcha_token: captchaToken,
        };
      }

      send(payload) {
        // text/plain keeps the request "simple" in CORS terms, so the browser skips
        // the preflight. Same JSON body either way — the receiving end just has to
        // parse it itself. Switchable from Theme settings → Price match.
        const contentType =
          this.dataset.contentType === 'text' ? 'text/plain;charset=UTF-8' : 'application/json';

        this.setLoading(true);

        fetch(this.webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': contentType },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Price match webhook responded ${response.status}`);
            this.showSuccess();
          })
          .catch((error) => {
            console.error('[price-match]', error);
            this.showBanner(this.messages.failed);
            this.resetCaptcha();
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      setLoading(isLoading) {
        this.submitButton.classList.toggle('btn--loading', isLoading);
        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      showSuccess() {
        this.hideBanner();
        this.form.classList.add('hidden');
        this.successPanel.classList.remove('hidden');
        // Focus stays inside the trapped modal and the panel's role="status"
        // announces the confirmation.
        this.successPanel.focus();
      }

      showBanner(message) {
        if (!this.errorBanner) return;
        this.errorBannerText.textContent = message;
        this.errorBanner.classList.remove('hidden');
      }

      hideBanner() {
        if (!this.errorBanner) return;
        this.errorBanner.classList.add('hidden');
        this.errorBannerText.textContent = '';
      }

      /* ========================
         HCAPTCHA
      ========================= */

      // Rendered on first open, never on page load: shoppers who don't open the
      // form never pay for the third-party script.
      onModalShown() {
        if (this.captchaWidgetId !== null) return;

        loadHcaptcha()
          .then((hcaptcha) => {
            if (this.captchaWidgetId !== null) return;
            this.captchaWidgetId = hcaptcha.render(this.captchaContainer, {
              sitekey: this.hcaptchaSiteKey,
            });
          })
          .catch((error) => {
            console.error('[price-match]', error);
          });
      }

      getCaptchaToken() {
        if (!window.hcaptcha || this.captchaWidgetId === null) return '';
        try {
          return window.hcaptcha.getResponse(this.captchaWidgetId) || '';
        } catch (error) {
          return '';
        }
      }

      resetCaptcha() {
        if (!window.hcaptcha || this.captchaWidgetId === null) return;
        try {
          window.hcaptcha.reset(this.captchaWidgetId);
        } catch (error) {
          /* widget already gone — nothing to reset */
        }
      }
    }
  );
}
